/**
 * Advanced settings metadata (design doc §14.4): one entry per GameConfig setting, grouped as in
 * the doc, with its label, the kind of input it needs and its allowed range. The web client
 * generates the ⚙ advanced settings panel from this list, so it has no zod and no rules of its
 * own. A test checks that the list covers every setting of the schema and that every range here
 * is accepted by the schema.
 *
 * Ranges: where the schema has a bound, it is used; where it has none (counts), the upper bound
 * is a generous UI limit, far outside anything tested (such values are Experimental anyway).
 */
import { ROLE_IDS, type RoleId } from "./roles.ts";

export const SETTING_GROUPS = [
  "teams",
  "clock",
  "handshakes",
  "cards",
  "council",
  "table",
  "testing",
] as const;
export type SettingGroup = (typeof SETTING_GROUPS)[number];

export const SETTING_GROUP_LABELS: Readonly<Record<SettingGroup, string>> = {
  teams: "Teams",
  clock: "Clock",
  handshakes: "Handshakes",
  cards: "Cards",
  council: "Council",
  table: "Table",
  testing: "Testing",
};

export interface SettingOption {
  readonly value: string | number;
  readonly label: string;
}

/** The input a setting needs. */
export type SettingControl =
  | { readonly type: "int"; readonly min: number; readonly max: number; readonly unit?: "%" }
  | { readonly type: "bool" }
  | { readonly type: "select"; readonly options: readonly SettingOption[] }
  /** A `{min, max}` range of whole numbers (the power budgets). */
  | { readonly type: "range"; readonly min: number; readonly max: number }
  /** A list of roles with counts, or `null` for a random setup within the budget. */
  | { readonly type: "roleList" }
  /** A set of roles (checkboxes). */
  | { readonly type: "roleSet" }
  /** A per-role limit: `{ base, byRole }` (Seals and Siphons per round). */
  | { readonly type: "perRole"; readonly min: number; readonly max: number }
  /** `"scramble"` or a percentage (Bug truth chance). */
  | { readonly type: "bugTruth" }
  /** `null` (off) or a whole number of seconds from `min` (omniscient delay). */
  | { readonly type: "optionalSeconds"; readonly min: number; readonly max: number }
  /** A short text, or `null` when empty. */
  | { readonly type: "text"; readonly maxLength: number }
  /** Forced player → role assignments (a Rigged game). */
  | { readonly type: "forcedRoles" };

export interface SettingDef {
  /** Dotted path in GameConfig, e.g. `clock.leakLimit`. */
  readonly path: string;
  readonly group: SettingGroup;
  readonly label: string;
  /** One line of help under the input. */
  readonly help?: string;
  readonly control: SettingControl;
  /**
   * False for settings of features that are not in this version (the setting exists in the
   * schema; the panel shows it disabled with "not available in this version").
   */
  readonly available: boolean;
}

const int = (min: number, max: number, unit?: "%"): SettingControl =>
  unit ? { type: "int", min, max, unit } : { type: "int", min, max };
const bool: SettingControl = { type: "bool" };
const select = (...options: [string | number, string][]): SettingControl => ({
  type: "select",
  options: options.map(([value, label]) => ({ value, label })),
});

/** Every advanced setting, in the order of §14.4. */
export const SETTINGS: readonly SettingDef[] = [
  // Teams
  {
    path: "teams.agents",
    group: "teams",
    label: "Number of Agents",
    control: int(1, 9),
    available: true,
  },
  {
    path: "teams.roles",
    group: "teams",
    label: "Roles in play",
    help: "Random within the power budget, or an explicit list with one role per player.",
    control: { type: "roleList" },
    available: true,
  },
  {
    path: "teams.rolePool",
    group: "teams",
    label: "Roles in the pool",
    help: "Roles a random setup may draw from.",
    control: { type: "roleSet" },
    available: true,
  },
  {
    path: "teams.duplicateRoles",
    group: "teams",
    label: "Duplicate roles",
    control: select(["standard", "Standard (§12.2)"], ["off", "Off"], ["unlimited", "Unlimited"]),
    available: true,
  },
  {
    path: "teams.enforcePowerBudget",
    group: "teams",
    label: "Power budget enforced",
    control: bool,
    available: true,
  },
  {
    path: "teams.powerBudget.delegate",
    group: "teams",
    label: "Delegate power budget",
    control: { type: "range", min: -10, max: 40 },
    available: true,
  },
  {
    path: "teams.powerBudget.syndicate",
    group: "teams",
    label: "Syndicate power budget",
    control: { type: "range", min: -10, max: 40 },
    available: true,
  },
  {
    path: "teams.safeBluffs",
    group: "teams",
    label: "Safe bluffs on the Guest List",
    control: int(0, 3),
    available: true,
  },
  {
    path: "teams.optionalRoles.rogue",
    group: "teams",
    label: "Optional role: Rogue",
    control: bool,
    available: true,
  },
  {
    path: "teams.optionalRoles.confessor",
    group: "teams",
    label: "Optional role: Confessor",
    control: bool,
    available: true,
  },
  // Clock
  {
    path: "clock.rounds",
    group: "clock",
    label: "Number of rounds",
    control: int(1, 12),
    available: true,
  },
  {
    path: "clock.leakLimit",
    group: "clock",
    label: "Leak limit",
    control: int(1, 40),
    available: true,
  },
  {
    path: "clock.finalTally",
    group: "clock",
    label: "Final Tally",
    control: bool,
    available: true,
  },
  {
    path: "clock.finalTallyPercent",
    group: "clock",
    label: "Final Tally threshold",
    help: "Share of the Leak limit the Syndicate needs at the Final Tally.",
    control: int(1, 100, "%"),
    available: true,
  },
  // Handshakes
  {
    path: "handshakes.maxPerRound",
    group: "handshakes",
    label: "Maximum handshakes per round",
    control: int(1, 10),
    available: true,
  },
  {
    path: "handshakes.minPerRound",
    group: "handshakes",
    label: "Minimum handshakes per round",
    help: "Players below this are listed as Wallflowers.",
    control: int(0, 10),
    available: true,
  },
  {
    path: "handshakes.wallflowers",
    group: "handshakes",
    label: "Wallflower listing",
    control: bool,
    available: true,
  },
  {
    path: "handshakes.repeatPartnerInRound",
    group: "handshakes",
    label: "Repeat handshakes with the same partner",
    control: bool,
    available: true,
  },
  // Cards
  {
    path: "cards.sealsPerRound",
    group: "cards",
    label: "Seals per round",
    help: "For every role, with exceptions by role.",
    control: { type: "perRole", min: 0, max: 5 },
    available: true,
  },
  {
    path: "cards.siphonsPerRound",
    group: "cards",
    label: "Siphons per round",
    help: "For every Agent, with exceptions by role.",
    control: { type: "perRole", min: 0, max: 5 },
    available: true,
  },
  {
    path: "cards.poisonVials",
    group: "cards",
    label: "Poison vials",
    control: int(0, 6),
    available: true,
  },
  {
    path: "cards.poisonsPerRound",
    group: "cards",
    label: "Poisons per round",
    help: "For the whole Syndicate.",
    control: int(0, 6),
    available: true,
  },
  {
    path: "cards.poisonFirstRound",
    group: "cards",
    label: "First round Poison can be used",
    control: int(1, 12),
    available: true,
  },
  {
    path: "cards.poisonDelay",
    group: "cards",
    label: "Poison delay",
    control: select([1, "Dies at the next Dusk"], [0, "Dies at the same Dusk"]),
    available: true,
  },
  {
    path: "cards.bugTruthChance",
    group: "cards",
    label: "Bug truth chance",
    help: "Scramble (the default), or the chance that a Bugged report is true anyway.",
    control: { type: "bugTruth" },
    available: true,
  },
  {
    path: "cards.analystMinHandshakes",
    group: "cards",
    label: "Handshakes an Analyst needs",
    control: int(1, 10),
    available: true,
  },
  // Council
  {
    path: "council.ballotVisibility",
    group: "council",
    label: "Ballot visibility",
    control: select(["open", "Open"], ["secret", "Secret (tally only)"]),
    available: true,
  },
  {
    path: "council.exileThresholdPercent",
    group: "council",
    label: "Exile threshold",
    help: "Share of living players whose votes an exile needs.",
    control: int(0, 100, "%"),
    available: true,
  },
  {
    path: "council.maxNominees",
    group: "council",
    label: "Maximum nominees",
    control: int(1, 10),
    available: true,
  },
  {
    path: "council.grandCouncilMaxNominees",
    group: "council",
    label: "Maximum nominees (Grand Council)",
    control: int(1, 10),
    available: true,
  },
  {
    path: "council.grandCouncil",
    group: "council",
    label: "Grand Council",
    control: select(["auto", "Automatic at 15+ players"], ["on", "On"], ["off", "Off"]),
    available: true,
  },
  {
    path: "council.exileReveals",
    group: "council",
    label: "What an exile reveals",
    control: select(["nothing", "Nothing"], ["team", "Team"], ["role", "Role"]),
    available: true,
  },
  {
    path: "council.ghostBallots",
    group: "council",
    label: "Ghost ballots",
    control: select(["one", "One"], ["none", "None"], ["unlimited", "Unlimited"]),
    available: true,
  },
  // Table
  {
    path: "table.timerChangesNeedApproval",
    group: "table",
    label: "Table approval for timer changes",
    control: bool,
    available: false,
  },
  {
    path: "table.coachMode",
    group: "table",
    label: "Coach mode",
    help: "Dispatches add a hint on how to read them.",
    control: bool,
    available: true,
  },
  {
    path: "table.bigScreenAllowed",
    group: "table",
    label: "Big screen allowed",
    control: bool,
    available: true,
  },
  {
    path: "table.spectatorsAllowed",
    group: "table",
    label: "Spectators allowed",
    control: bool,
    available: false,
  },
  {
    path: "table.omniscientDelaySec",
    group: "table",
    label: "Omniscient spectator view",
    control: { type: "optionalSeconds", min: 120, max: 1800 },
    available: false,
  },
  // Testing
  {
    path: "testing.seed",
    group: "testing",
    label: "Fixed random seed",
    help: "Only the host sees it; it is shown to everyone at the end.",
    control: { type: "text", maxLength: 64 },
    available: true,
  },
  {
    path: "testing.experimentTag",
    group: "testing",
    label: "Experiment tag",
    help: "Labels the game in the analytics export.",
    control: { type: "text", maxLength: 64 },
    available: true,
  },
  {
    path: "testing.forcedRoles",
    group: "testing",
    label: "Forced roles (Rigged game)",
    help: "Everyone sees that the game is rigged, and it is left out of balance statistics.",
    control: { type: "forcedRoles" },
    available: true,
  },
];

/** Human-readable labels for every setting, keyed by dotted path. */
export const SETTING_LABELS: Readonly<Record<string, string>> = {
  ...Object.fromEntries(SETTINGS.map((s) => [s.path, s.label])),
  "cards.sealsPerRound.base": "Seals per round",
  "cards.siphonsPerRound.base": "Siphons per round",
};

/** Settings of one group, in panel order. */
export function settingsIn(group: SettingGroup): SettingDef[] {
  return SETTINGS.filter((s) => s.group === group);
}

/** Roles a per-role limit can name (every role). */
export const PER_ROLE_IDS: readonly RoleId[] = ROLE_IDS;

/** Reads a dotted path from a plain object (undefined when missing). */
export function getPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const key of path.split(".")) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

/**
 * Returns a copy of `obj` with `value` at `path`, creating objects on the way; `undefined`
 * removes the key (and parents left empty), so a setting goes back to its default.
 */
export function setPath<T extends object>(obj: T, path: string, value: unknown): T {
  const keys = path.split(".");
  const out: Record<string, unknown> = { ...(obj as Record<string, unknown>) };
  const [head, ...rest] = keys as [string, ...string[]];
  if (rest.length === 0) {
    if (value === undefined) delete out[head];
    else out[head] = value;
    return out as T;
  }
  const child = out[head];
  const next = setPath(
    child !== null && typeof child === "object" && !Array.isArray(child) ? child : {},
    rest.join("."),
    value,
  );
  if (value === undefined && Object.keys(next).length === 0) delete out[head];
  else out[head] = next;
  return out as T;
}
